import React from 'react';
import { Link } from 'react-router-dom';
import { LegalLayout, OPERATOR } from './LegalLayout';
import { useSiteSettings } from '../lib/useSiteSettings';

export default function LegalTerms() {
    const { settings: site } = useSiteSettings();
    return (
        <LegalLayout title="Termeni și Condiții" lastUpdated="30 mai 2026">
            <p>
                Bine ai venit pe <strong>{OPERATOR.platform}</strong> (denumită în continuare „Platforma"),
                serviciu oferit de <strong>{OPERATOR.legal_name}</strong>, CUI {OPERATOR.cui}, înregistrată
                la Registrul Comerțului sub nr. {OPERATOR.reg_com} (denumită în continuare „Furnizor", „noi").
            </p>
            <p>
                Prin crearea unui cont și utilizarea Platformei, accepți integral termenii și condițiile
                de mai jos. Dacă nu ești de acord, te rugăm să nu folosești serviciul.
            </p>

            <h2>1. Definiții</h2>
            <ul>
                <li><strong>Utilizator / Restaurant:</strong> persoană juridică (SRL, SA, PFA) care își înregistrează restaurantul pe Platformă</li>
                <li><strong>Administrator cont:</strong> persoană fizică desemnată de Restaurant pentru gestionarea contului</li>
                <li><strong>Abonament:</strong> dreptul de utilizare al serviciilor Platformei conform Planului ales</li>
                <li><strong>Trial:</strong> perioadă de {site.trial_days} zile gratuită oferită la înregistrare</li>
                <li><strong>Plan Standard / Pro:</strong> niveluri de funcționalitate descrise public pe Platformă</li>
            </ul>

            <h2>2. Servicii oferite</h2>
            <p>Platforma oferă restaurantelor instrumente digitale pentru:</p>
            <ul>
                <li>Meniu QR digital scanabil la masă</li>
                <li>Gestionare comenzi în timp real (client, ospătar, bucătărie)</li>
                <li>Gestionare mese și echipă</li>
                <li>Statistici de vânzări</li>
                <li>Branding personalizat și meniu multilingv</li>
            </ul>
            <p>
                Furnizorul își rezervă dreptul de a adăuga, modifica sau elimina funcționalități,
                cu notificare prealabilă pentru modificări majore.
            </p>

            <h2>3. Cont și autentificare</h2>
            <ul>
                <li>Înregistrarea necesită un email valid (verificat prin cod OTP), număr de telefon, parolă puternică și date complete de facturare (CUI, denumire legală, sediu)</li>
                <li>Utilizatorul e responsabil pentru păstrarea confidențialității credențialelor</li>
                <li>Recomandăm activarea autentificării în 2 pași (2FA) prin TOTP sau WhatsApp</li>
                <li>Datele introduse trebuie să fie corecte și actuale — Utilizatorul are obligația să le actualizeze</li>
            </ul>

            <h2>4. Perioada de probă (Trial)</h2>
            <ul>
                <li>La înregistrare, beneficiezi de <strong>{site.trial_days} zile gratuit</strong>, fără card de credit</li>
                <li>După expirarea trial-ului, contul devine inactiv până la activarea unui abonament plătit</li>
                <li>Datele introduse în trial se păstrează 30 zile după expirare; după aceea pot fi șterse</li>
            </ul>

            <h2>5. Abonament și plată</h2>
            <ul>
                <li>Prețurile sunt afișate transparent pe pagina <Link to="/#pricing" style={{ color: 'var(--amber)' }}>Planuri</Link>, în RON, fără TVA inclus</li>
                <li>TVA-ul (21%) se adaugă conform legii fiscale române</li>
                <li>Cicluri facturare disponibile: lunar, 3 luni, 6 luni (cu reducere 8%), anual (cu reducere 15%)</li>
                <li>Plata se face în avans pentru perioada aleasă</li>
                <li>Facturile fiscale se emit automat prin SmartBill la confirmarea plății și se trimit pe email-ul de facturare</li>
                <li>Plățile se procesează prin Revolut Merchant — Furnizorul nu stochează datele cardului</li>
                <li>Abonamentul NU se reînnoiește automat — utilizatorul trebuie să plătească explicit pentru extindere</li>
            </ul>

            <h2>6. Returnări și retragere</h2>
            <p>
                Conform Legii nr. 365/2002 și OUG 34/2014, dreptul de retragere de 14 zile NU se aplică
                serviciilor digitale a căror executare a început cu acordul expres al consumatorului
                (creare cont + utilizare). Plățile pentru perioade deja consumate NU se rambursează.
            </p>
            <p>
                În caz de probleme tehnice grave imputabile Furnizorului, putem oferi compensare pro-rata
                sau extindere abonament — analizat caz cu caz la <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>.
            </p>

            <h2>7. Disponibilitatea serviciului</h2>
            <ul>
                <li>Țintim disponibilitate 99% (uptime), excluzând mentenanțele planificate</li>
                <li>Mentenanțele majore se anunță cu minim 24 ore înainte pe email</li>
                <li>Nu garantăm disponibilitate 100% — fluctuațiile rețelei, problemele furnizorilor terți (Mongo Atlas, Render, procesatori plăți) pot cauza întreruperi</li>
            </ul>

            <h2>8. Obligațiile Utilizatorului</h2>
            <p>Te angajezi să:</p>
            <ul>
                <li>NU folosești Platforma în scopuri ilegale</li>
                <li>NU încarci conținut care încalcă drepturi de autor, mărci sau alte drepturi</li>
                <li>NU încerci să accesezi date ale altor restaurante (multi-tenant)</li>
                <li>NU faci reverse engineering, scrape masiv sau alte acțiuni care afectează disponibilitatea</li>
                <li>NU revinzi acces la Platformă fără acord scris</li>
                <li>Plătești la timp facturile emise</li>
            </ul>

            <h2>9. Drepturi de proprietate intelectuală</h2>
            <ul>
                <li>Codul sursă, designul, logo-urile Platformei aparțin Furnizorului</li>
                <li>Conținutul introdus de Utilizator (meniu, poze, descrieri) rămâne proprietatea Utilizatorului — Furnizorul are doar licență limitată să-l afișeze pentru funcționarea serviciului</li>
                <li>La ștergerea contului, conținutul Utilizatorului se șterge în 30 zile</li>
            </ul>

            <h2>10. Limitări de răspundere</h2>
            <p>
                În limita maximă permisă de lege, răspunderea Furnizorului față de Utilizator pentru orice
                pretenție legată de Platformă este limitată la suma plătită de Utilizator în ultimele
                12 luni. Nu suntem responsabili pentru pierderi indirecte (profit nerealizat, reputație
                etc.) sau pentru date pierdute din cauza neînregistrării unor backup-uri proprii.
            </p>

            <h2>11. Suspendare și reziliere</h2>
            <ul>
                <li>Furnizorul poate suspenda contul în caz de neplată după trial sau în caz de încălcare a termenilor</li>
                <li>Utilizatorul poate șterge contul oricând din Setări sau cerând la <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a></li>
                <li>Datele se păstrează 30 zile după ștergere pentru reactivare; după aceea se anonimizează ireversibil (mai puțin datele fiscale, păstrate 10 ani conform legii)</li>
            </ul>

            <h2>12. Modificări ale termenilor</h2>
            <p>
                Ne rezervăm dreptul de a modifica termenii. Modificările se comunică prin email cu minim
                30 zile înainte de intrarea în vigoare. Continuarea utilizării Platformei după notificare
                implică acceptarea noii versiuni.
            </p>

            <h2>13. Soluționarea disputelor — ANPC / SAL</h2>
            <p>
                Disputele se soluționează amiabil prin email/telefon. În caz de eșec, Utilizatorul are
                dreptul de a apela la:
            </p>
            <ul>
                <li><strong>ANPC</strong> (Autoritatea Națională pentru Protecția Consumatorilor): <a href="https://anpc.ro" target="_blank" rel="noopener noreferrer">anpc.ro</a></li>
                <li><strong>Soluționarea Alternativă a Litigiilor (SAL)</strong>: <a href="https://anpc.ro/ce-este-sal/" target="_blank" rel="noopener noreferrer">anpc.ro/ce-este-sal</a></li>
                <li><strong>Platforma SOL UE</strong>: <a href="https://ec.europa.eu/consumers/odr" target="_blank" rel="noopener noreferrer">ec.europa.eu/consumers/odr</a></li>
            </ul>

            <h2>14. Legea aplicabilă și instanța competentă</h2>
            <p>
                Acești termeni se supun legilor României. Pentru disputele nesoluționate amiabil,
                instanțele competente sunt cele de la sediul Furnizorului ({OPERATOR.address.split(',').slice(-2).join(',').trim()}).
            </p>

            <h2>15. Contact</h2>
            <p>
                <strong>{OPERATOR.legal_name}</strong>
                <br />CUI {OPERATOR.cui} · {OPERATOR.reg_com}
                <br />{OPERATOR.address}
                <br />Email: <a href={`mailto:${OPERATOR.email}`}>{OPERATOR.email}</a>
            </p>
        </LegalLayout>
    );
}
